import { Anchor, Divider, Drawer, ScrollArea, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { HELP_DOCS, PARSED_DOCS } from './content';
import { HELP_MAP, findHelpMapEntry, type HelpSectionRef } from './helpMap';
import { extractParsedSection, type DocSection } from './sections';
import { Markdown } from './markdown';

function refKey(ref: HelpSectionRef): string {
  return `${ref.doc}:${ref.slug}`;
}

/** Every section any HELP_MAP entry references, extracted ONCE at module
 * scope from content.ts's precomputed PARSED_DOCS (both are static, so this
 * is a one-off pure computation at bundle-eval time — a handful of array
 * slices, no markdown parsing). Rendering the drawer, and navigating between
 * routes with it closed, never re-parses a document. */
const RESOLVED_SECTIONS: ReadonlyMap<string, DocSection> = (() => {
  const map = new Map<string, DocSection>();
  for (const entry of HELP_MAP) {
    for (const ref of entry.sections) {
      const key = refKey(ref);
      if (map.has(key)) continue;
      const section = extractParsedSection(PARSED_DOCS[ref.doc], ref.slug);
      if (section) map.set(key, section);
    }
  }
  return map;
})();

interface HelpDrawerProps {
  /** Current route pathname (HelpButton.tsx reads this via `useRouterState`
   * and passes it down) — kept as a plain prop rather than a router hook
   * inside this component so HelpDrawer stays trivially testable/renderable
   * in isolation (see HelpDrawer.test.tsx). */
  pathname: string;
  opened: boolean;
  onClose: () => void;
}

/** Contextual help drawer (right side, opened from the header's HelpButton):
 * renders only the guide section(s) mapped to the current route (helpMap.ts,
 * longest-prefix-wins, falling back to the Home mapping for any unmapped
 * route), each followed by a link into the full /help centre at that
 * section's exact anchor. The body is only rendered while the drawer is
 * open — closed, this component costs a map lookup per route change and
 * mounts no markdown at all. */
export function HelpDrawer({ pathname, opened, onClose }: HelpDrawerProps) {
  const entry = findHelpMapEntry(pathname);

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="lg" title={`Help — ${entry.title}`}>
      {opened && (
        <ScrollArea.Autosize mah="calc(100vh - 120px)">
          <Stack gap="lg">
            {entry.sections.map((ref, index) => {
              const section = RESOLVED_SECTIONS.get(refKey(ref));
              return (
                <div key={refKey(ref)}>
                  {index > 0 && <Divider mb="lg" />}
                  {section ? (
                    <Markdown content={section.markdown} headings={section.headings} />
                  ) : (
                    // Should never happen in a shipped build — helpMap.test.ts's
                    // anchor-integrity test fails CI first if a doc edit ever
                    // removes/renames a heading a HELP_MAP entry points at. This
                    // is a safety net, not the primary guard.
                    <Text c="dimmed" size="sm">
                      This section could not be found in {HELP_DOCS[ref.doc].title} (slug: {ref.slug}).
                    </Text>
                  )}
                  {/* onClick closes the drawer as the router navigates — without
                   * it the drawer (whose `opened` state lives up in HelpButton,
                   * inside the persistent Shell) stays open on top of /help. */}
                  <Anchor
                    component={Link}
                    to="/help"
                    hash={ref.slug}
                    size="sm"
                    fw={600}
                    mt="xs"
                    display="inline-block"
                    onClick={onClose}
                  >
                    Open in full guide →
                  </Anchor>
                </div>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Drawer>
  );
}
